import _ from "lodash"
import {
  createMRTColumnHelper,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table"
import { useMemo } from "react"

import { ExploreResponses } from "@costory/types/endpoints/explorer"

import dayjs from "@costory/shared/dayjs"
import { tickFormatter } from "@costory/shared/utils/charts"
import { formatNumber } from "@costory/shared/utils/format"

import {
  ToolbarInternalActions,
  TopToolbarCustomActions,
} from "@costory/front/components/charts/TableExplorer"
import { CustomCopyCellButton } from "@costory/front/components/common/CustomCopyCellButtton"
import { DrillDownCell } from "@costory/front/components/table/DrillDownCell"
import { NumberCell } from "@costory/front/components/table/NumberCell"
import { TruncatedHeader } from "@costory/front/components/table/TruncatedHeader"
import { useChartContext } from "@costory/front/contexts/ChartContext"
import { truncateLabel } from "@costory/front/utils/charts"
import { getCommonTableStyleProps } from "@costory/front/utils/table"

export const DataTableExplorer = ({
  data,
  drillDownInto,
  isDataTable,
  setIsDataTable,
}: {
  data: ExploreResponses.ExplorerData["dataTable"]
  drillDownInto: (value: string, mode: "sameWindow" | "newTab") => void
  isDataTable: boolean
  setIsDataTable: (checked: boolean) => void
}) => {
  const { filterForm, groupByOptions } = useChartContext()
  const { groupBy, aggBy, currency } = filterForm.getValues()
  const valueLabel =
    groupByOptions.flatMap((g) => g.items).find((i) => i.value === groupBy)
      ?.label || "Value"

  const columns = useMemo(() => {
    const columnHelper =
      createMRTColumnHelper<ExploreResponses.ExplorerData["dataTable"][0]>()
    const sampleRow = data[0] || {}
    const allKeys = Object.keys(sampleRow)
    const calculateRange = (
      key: keyof ExploreResponses.ExplorerData["dataTable"],
    ) => {
      const values = data
        .map((item) => Number(item[key as string]))
        .filter((value): value is number => !isNaN(value))

      return {
        min: Math.min(...values),
        max: Math.max(...values),
      }
    }
    return [
      columnHelper.accessor("group_by", {
        header: truncateLabel(valueLabel, 20),
        Header: () => <TruncatedHeader label={valueLabel} />,
        filterVariant: "autocomplete",
        size: Math.min(300, Math.max(valueLabel.length * 12, 350)),
        Cell: ({ cell }) => (
          <DrillDownCell
            value={cell.getValue<string>()}
            drillDownInto={drillDownInto}
          />
        ),
      }),
      ..._(allKeys)
        .map((val) => ({ val, date: dayjs(val, "YYYY-MMM") }))
        .sortBy((el) => el.date)
        .value()
        .map((el) => el.val)
        .filter((key) => key !== "group_by")
        .map((key) => {
          const { min, max } = calculateRange(
            key as keyof ExploreResponses.ExplorerData["dataTable"],
          )
          return columnHelper.accessor(key, {
            header: tickFormatter(key, aggBy),
            size: 200,
            filterVariant: "range-slider",
            mantineFilterRangeSliderProps: {
              min: min - 1,
              max: max + 2,
              label: (value) => formatNumber({ value }),
            },
            Cell: ({ cell }) => (
              <CustomCopyCellButton cell={cell}>
                <NumberCell
                  value={Number(cell.getValue())}
                  currency={currency}
                />
              </CustomCopyCellButton>
            ),
          })
        }),
    ]
  }, [data, valueLabel, aggBy, currency, drillDownInto])

  const table = useMantineReactTable<
    ExploreResponses.ExplorerData["dataTable"][0]
  >({
    columns,
    data,
    enableColumnPinning: true,
    initialState: {
      columnPinning: {
        left: ["group_by"],
      },
    },
    enableColumnVirtualization: true,
    columnVirtualizerOptions: {
      horizontal: true,
    },
    renderToolbarInternalActions: ({ table }) => (
      <ToolbarInternalActions table={table} data={data} />
    ),
    renderTopToolbarCustomActions: () => (
      <TopToolbarCustomActions
        isDataTable={isDataTable}
        onToggleTableView={(checked) => setIsDataTable(checked)}
      />
    ),
    mantineTableBodyRowProps: ({ row }) => ({
      style:
        row.getVisibleCells().length === 0 ? { display: "none" } : undefined,
    }),
    mantineTableHeadProps: ({ table }) => ({
      style:
        table.getVisibleLeafColumns().length === 0
          ? { display: "none" }
          : undefined,
    }),
    ...getCommonTableStyleProps(),
  })

  return <MantineReactTable table={table} />
}
